import { Component, Input, Output, ViewChild, ElementRef, AfterViewInit, OnChanges, DoCheck, SimpleChanges, EventEmitter, Injectable, OnInit, OnDestroy, Renderer2, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DOCUMENT } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { TgSettingsService } from '../../../../services/TG-Setting_Services/tg-settings.service';
import { TranslationInitService } from 'app/services/Translate_Services/translation-init.service';

// -------------------------------------------------------------------------
// 1. STORE (Centralized Logic Brain - STRICT LEGACY PAYLOAD PARITY)
// -------------------------------------------------------------------------
@Injectable()
export class DateFormatStore {
  code: string = '1';
  viewMode: 'date' | 'month' = 'date';
  selectionMode: 'single' | 'range' = 'single';
  rangeSeparator: string = ' to ';

  constructor(private translationInit: TranslationInitService) {}

  // Helper 1: Get Separator (Matches legacy 'separatedBy' logic)
  private get separator(): string {
    if (['4', '7', '10', '12'].includes(this.code)) return '-';
    if (['5', '8', '11'].includes(this.code)) return '.';
    return '/'; // Defaults to slash for 1, 2, 3, 6, 9
  }

  // Helper 2: Get Order Layout (Matches legacy format buckets)
  private get formatType(): 'MDY' | 'DMY' | 'YMD' {
    if (['1', '6', '7', '8'].includes(this.code)) return 'MDY';
    if (['9', '10', '11', '12'].includes(this.code)) return 'YMD';
    // Fallback covers 2, 3, 4, 5
    return 'DMY'; 
  }

  private formatSingleDate(date: Date): string {
    const d = date.getDate().toString();
    const m = (date.getMonth() + 1).toString();
    const y = date.getFullYear();
    const sep = this.separator;

    if (this.viewMode === 'month') {
      return this.formatType === 'YMD' ? `${y}${sep}${m}` : `${m}${sep}${y}`;
    }

    if (this.formatType === 'MDY') return `${m}${sep}${d}${sep}${y}`;
    if (this.formatType === 'YMD') return `${y}${sep}${m}${sep}${d}`;
    return `${d}${sep}${m}${sep}${y}`;
  }

  formatDate(date: any): string {
    if (!date) return '';
    
    // For Range Dates
    if (this.selectionMode === 'range' && Array.isArray(date)) {
      if (date[0] && date[1]) {
        return `${this.formatSingleDate(date[0])}%2C${this.formatSingleDate(date[1])}`;
      } else if (date[0]) {
        return this.formatSingleDate(date[0]); 
      }
      return '';
    }
    
    // For Single Dates
    return this.formatSingleDate(date as Date);
  }

  private parseSingleDate(value: string): Date | null {
    if (!value || value.indexOf(this.separator) === -1) return null;
    const parts = value.split(this.separator);
    
    if (this.viewMode === 'month') {
        if (parts.length !== 2) return null;
        const mIdx = this.formatType === 'YMD' ? 1 : 0;
        const yIdx = this.formatType === 'YMD' ? 0 : 1;
        const m = Number(parts[mIdx]) - 1;
        const y = Number(parts[yIdx]);
        if (isNaN(m) || isNaN(y)) return null;
        return new Date(y, m, 1);
    }

    if (parts.length !== 3) return null;
    let mIdx = 1, dIdx = 0, yIdx = 2; // Default DMY

    // Matches legacy index mappings
    if (this.formatType === 'MDY') { mIdx = 0; dIdx = 1; yIdx = 2; }
    if (this.formatType === 'YMD') { mIdx = 1; dIdx = 2; yIdx = 0; }

    const d = Number(parts[dIdx]);
    const m = Number(parts[mIdx]) - 1; // JS Months are 0-11
    const y = Number(parts[yIdx]);

    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    return new Date(y, m, d);
  }

  parseDate(value: string): any {
    if (!value) return null;

    if (this.selectionMode === 'range') {
      const parts = value.split('%2C');
      if (parts.length === 2) {
        const d1 = this.parseSingleDate(parts[0]);
        const d2 = this.parseSingleDate(parts[1]);
        if (d1 && d2) return [d1, d2];
      }
      const single = this.parseSingleDate(value);
      return single ? [single, null] : null;
    }

    return this.parseSingleDate(value);
  }

  getPlaceholder(): string {
    const sep = this.separator;
    let base = '';
    
    if (this.viewMode === 'month') {
        base = this.formatType === 'YMD' ? `yyyy${sep}mm` : `mm${sep}yyyy`;
    } else {
        let translationKey = 'PlaceHolder_DateFormat2'; // Default: d/m/yyyy
        
        if (this.formatType === 'MDY') {
            if (sep === '/') translationKey = 'PlaceHolder_DateFormat1';
            else if (sep === '-') translationKey = 'PlaceHolder_DateFormat5';
            else if (sep === '.') translationKey = 'PlaceHolder_DateFormat6';
        } else if (this.formatType === 'YMD') {
            if (sep === '/') translationKey = 'PlaceHolder_DateFormat7';
            else if (sep === '-') translationKey = 'PlaceHolder_DateFormat8';
            else if (sep === '.') translationKey = 'PlaceHolder_DateFormat9';
        } else { 
            if (sep === '/') translationKey = 'PlaceHolder_DateFormat2';
            else if (sep === '-') translationKey = 'PlaceHolder_DateFormat3';
            else if (sep === '.') translationKey = 'PlaceHolder_DateFormat4';
        }

        base = this.translationInit.getInstantTranslation(translationKey);

        if (!base || base === translationKey) {
            if (this.formatType === 'MDY') base = `m${sep}d${sep}yyyy`;
            else if (this.formatType === 'YMD') base = `yyyy${sep}m${sep}d`;
            else base = `d${sep}m${sep}yyyy`;
        }
    }

    return this.selectionMode === 'range' ? `${base} ${this.rangeSeparator} ${base}` : base;
  }

  getPrimeNGFormat(): string {
    const sep = this.separator;
    if (this.viewMode === 'month') {
        return this.formatType === 'YMD' ? `yy${sep}mm` : `mm${sep}yy`;
    }
    if (this.formatType === 'MDY') return `mm${sep}dd${sep}yy`;
    if (this.formatType === 'YMD') return `yy${sep}mm${sep}dd`;
    return `dd${sep}mm${sep}yy`;
  }
}

// -------------------------------------------------------------------------
// 2. COMPONENT
// -------------------------------------------------------------------------
@Component({
  selector: 'app-primeng-date-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CalendarModule
  ],
  templateUrl: './primeng-date-input.component.html',
  styleUrls: ['./primeng-date-input.component.css'],
  encapsulation: ViewEncapsulation.None, 
  providers: [DateFormatStore]
})
export class PrimengDateInputComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() control!: FormControl<any>;
  @Input() inputId = 'primeng-date';
  @Input() screenReaderLabel = 'Date input';
  @Input() ariaLabelledBy?: string;
  @Input() disableWeekends = false;
  @Input() dateFormat: string = '1';

  @Input() selectionMode: 'single' | 'range' = 'single';
  @Input() view: 'date' | 'month' = 'date';

  @Output() dateStringChange = new EventEmitter<string>();

  placeholder = 'mm/dd/yyyy';
  primeDateFormat = 'mm/dd/yy';
  disabledDays: number[] = [];
  calendarLocale: string = 'en-US'; 

  @ViewChild('tgCalendar', { read: ElementRef }) calendarRef!: ElementRef;
  @ViewChild('tgCalendar') pCalendarInstance!: any; 

  a11yAnnouncement: string = '';
  a11yDateAnnouncement: string = '';

  constructor(
    private formatStore: DateFormatStore,
    public tgsettings: TgSettingsService,
    private translationInit: TranslationInitService,
    private renderer: Renderer2,
    private el: ElementRef,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    if (this.tgsettings && typeof (this.tgsettings as any).getdateFormat === 'function') {
       this.formatStore.code = (this.tgsettings as any).getdateFormat().toString();
       this.dateFormat = this.formatStore.code;
    } else {
       this.formatStore.code = this.dateFormat;
    }
    
    const translatedTo = this.translationInit.getInstantTranslation('Label_To');
    this.formatStore.rangeSeparator = translatedTo;
    
    if (this.tgsettings && typeof this.tgsettings.getLocale === 'function') {
       this.calendarLocale = this.tgsettings.getLocale();
    }

    this.updateFormatLogic();

    const styles = this.tgsettings.getBrandingConfig && typeof this.tgsettings.getBrandingConfig === 'function' 
        ? this.tgsettings.getBrandingConfig() 
        : null;
    this.applyThemeVariables(styles);
  }

  ngAfterViewInit(): void {
    // Input and trigger button attributes that don't depend on the overlay
    // can be set once during initialization.
    this.setupStaticAccessibility();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateFormat']) {
       if (this.tgsettings && typeof (this.tgsettings as any).getdateFormat === 'function') {
          this.formatStore.code = (this.tgsettings as any).getdateFormat().toString();
          this.dateFormat = this.formatStore.code;
       } else {
          this.formatStore.code = this.dateFormat;
       }
       if (this.tgsettings && typeof this.tgsettings.getLocale === 'function') {
          this.calendarLocale = this.tgsettings.getLocale();
       }
       this.updateFormatLogic();
    }
    if (changes['selectionMode'] || changes['view']) {
      this.updateFormatLogic();
    }
    if (changes['disableWeekends']) {
      this.disabledDays = this.disableWeekends && this.view === 'date' ? [0, 6] : [];
    }
  }

  ngOnDestroy(): void {}

  private updateFormatLogic() {
    this.formatStore.viewMode = this.view;
    this.formatStore.selectionMode = this.selectionMode;

    this.placeholder = this.formatStore.getPlaceholder();
    this.primeDateFormat = this.formatStore.getPrimeNGFormat();
    
    this.disabledDays = this.disableWeekends && this.view === 'date' ? [0, 6] : [];
  }

  onDateSelect(eventClickedDate: any) {
    const currentValue = this.control.value;

    if (this.selectionMode === 'range' && Array.isArray(currentValue)) {
      const formattedDate = this.formatStore.formatDate(currentValue);
      if (currentValue[0] && currentValue[1]) {
        this.announceDate(`Selected range from ${this.formatStore.formatDate(currentValue[0])} to ${this.formatStore.formatDate(currentValue[1])}`);
      }
      this.dateStringChange.emit(formattedDate);
      return;
    }

    if (currentValue instanceof Date && !isNaN(currentValue.getTime())) {
      const formattedDate = this.formatStore.formatDate(currentValue);
      this.announceDate(`Selected date is ${formattedDate}`);
      this.dateStringChange.emit(formattedDate);
    }
  }

  private announceDate(message: string) {
    // Use the assertive live region so the announcement immediately interrupts
    // the screen reader before the parent DOM update (triggered by dateStringChange.emit)
    // can cause the screen reader to read the main region instead.
    this.a11yDateAnnouncement = '';
    requestAnimationFrame(() => {
      this.a11yDateAnnouncement = message;
      setTimeout(() => { this.a11yDateAnnouncement = ''; }, 3000);
    });
  }

  onManualInput(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    const parsedDate = this.formatStore.parseDate(inputValue);

    if (parsedDate) {
      if (Array.isArray(parsedDate)) {
          if (!isNaN(parsedDate[0]?.getTime())) {
            this.control.setValue(parsedDate, { emitEvent: false });
            const formattedDate = this.formatStore.formatDate(parsedDate);
            this.dateStringChange.emit(formattedDate);
            this.announceToScreenReader(`Valid date entered: ${formattedDate}`);
          }
      } else if (!isNaN(parsedDate.getTime())) {
          this.control.setValue(parsedDate, { emitEvent: false });
          const formattedDate = this.formatStore.formatDate(parsedDate);
          this.dateStringChange.emit(formattedDate);
          this.announceToScreenReader(`Valid date entered: ${formattedDate}`);
      }
    } else {
      this.dateStringChange.emit("");
    }
  }
  private applyThemeVariables(styles: any) {
    const host = this.document.documentElement;
    
    // Ensure styles is defined; if null, use defaults
    if (!styles) styles = {};
    
    host.style.setProperty('--tg-bg', styles.bg );
    host.style.setProperty('--tg-text', styles.text );
    host.style.setProperty('--tg-focus', styles.focus );
    host.style.setProperty('--tg-font', styles.font );
    host.style.setProperty('--tg-hover-bg', styles.hoverBg );
    host.style.setProperty('--tg-control-height', styles.controlHeight);
    host.style.setProperty('--tg-active-text', styles.activeText );
    host.style.setProperty('--tg-control-max-width', styles.controlMaxWidth );
    host.style.setProperty('--tg-header-bg', styles.headerBg || styles.focus );
  }

  // ==========================================
  // STATIC ACCESSIBILITY (Runs Once)
  // ==========================================
  private setupStaticAccessibility() {
    if (!this.calendarRef) return;
    const root = this.calendarRef.nativeElement;

    // INPUT FIXES
    const inputEl = root.querySelector('.p-inputtext');
    if (inputEl) {
      this.renderer.setAttribute(inputEl, 'id', this.inputId);
      const labelledBy = this.ariaLabelledBy || (this.inputId + '_label');
      this.renderer.setAttribute(inputEl, 'aria-labelledby', labelledBy);
      this.renderer.setAttribute(inputEl, 'aria-describedby', this.inputId + '_desc');
      this.renderer.setAttribute(inputEl, 'autocomplete', 'off');
      this.renderer.setAttribute(inputEl, 'autocorrect', 'off');
      this.renderer.setAttribute(inputEl, 'autocapitalize', 'off');
      this.renderer.setAttribute(inputEl, 'spellcheck', 'false');
      this.renderer.setAttribute(inputEl, 'class', 'control ng-pristine ng-valid ng-star-inserted ng-touched');
    }

    // TRIGGER BUTTON FIX
    const buttonEl = root.querySelector('.p-datepicker-trigger');
    if (buttonEl) {
      this.renderer.setAttribute(buttonEl, 'aria-label', `Open calendar for ${this.screenReaderLabel}`);
      this.renderer.setAttribute(buttonEl, 'aria-haspopup', 'dialog');
      this.renderer.setAttribute(buttonEl, 'aria-expanded', 'false');

      if (!buttonEl.getAttribute('tabindex')) {
        this.renderer.setAttribute(buttonEl, 'tabindex', '0');
      }
    }
  }

  // ==========================================
  // DYNAMIC ACCESSIBILITY (Triggered by onShow)
  // ==========================================
  public applyAccessibilityFixes() {
    // 1. Update trigger button state to true
    if (this.calendarRef) {
      const buttonEl = this.calendarRef.nativeElement.querySelector('.p-datepicker-trigger');
      if (buttonEl) this.renderer.setAttribute(buttonEl, 'aria-expanded', 'true');
    }

    // 2. Fetch Panel (Using setTimeout briefly to ensure PrimeNG has fully rendered the DOM)
    setTimeout(() => {
      const panel = this.document.querySelector('.tg-solid-calendar-panel') as HTMLElement;
      if (!panel) return;

      // Set Dialog Role
      this.renderer.setAttribute(panel, 'role', 'dialog');
      this.renderer.setAttribute(panel, 'aria-modal', 'true');
      this.renderer.setAttribute(panel, 'aria-label', `Calendar for ${this.screenReaderLabel}`);

      // Safe Keyboard Binding
      if (!panel.hasAttribute('data-tg-keys-bound')) {
        this.renderer.setAttribute(panel, 'data-tg-keys-bound', 'true');
        panel.addEventListener('keydown', (event: KeyboardEvent) => {
          const activeEl = this.document.activeElement as HTMLElement;
          if (!activeEl) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activeEl.click();
          }
        });
      }

      this.fixPanelElements(panel);
      this.handleFocusManagement(panel);
      this.bindHeaderButtons(panel);

      const currentMonthYear = panel.querySelector('.p-datepicker-title')?.textContent?.trim() || '';
      this.announceToScreenReader(
        `Calendar opened. ${currentMonthYear}. Use arrow keys to navigate dates. Press Escape to close the calendar.`
      );

    }, 50); // Give PrimeNG a brief moment to append the overlay
  }

 // Called by onMonthChange / onYearChange — re-runs panel fixes but does NOT
  // re-announce "Calendar opened" because bindNavMonthAnnouncement handles the navigation announcement.
  public onCalendarNavigate() {
    setTimeout(() => {
      const panel = this.document.querySelector('.tg-solid-calendar-panel') as HTMLElement;
      if (!panel) return;
      this.fixPanelElements(panel);
      this.handleFocusManagement(panel);
      this.bindHeaderButtons(panel);
    }, 50);
  }

 private bindNavMonthAnnouncement(btn: HTMLElement, panel: HTMLElement) {
    if (btn.hasAttribute('data-tg-month-announce-bound')) return;
    btn.setAttribute('data-tg-month-announce-bound', 'true');

    btn.addEventListener('click', () => {
      // Wait two frames so PrimeNG has finished updating the calendar header
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const monthYear = panel.querySelector('.p-datepicker-title')?.textContent?.trim();
          if (monthYear) {
            this.announceToScreenReader(monthYear);
          }
        });
      });
    });
  }

 private bindNavButtonKeyboardTooltip(btn: HTMLElement) {
    if (btn.hasAttribute('data-tg-tooltip-bound')) return;
    btn.setAttribute('data-tg-tooltip-bound', 'true');

    // Capture label and remove the native title attribute so the browser's own
    // tooltip does not compete. We re-create an identical tooltip in JS for both
    // mouseenter and focusin so hover and keyboard always show the same thing.
    const label = btn.getAttribute('title') || btn.getAttribute('aria-label') || '';
    btn.removeAttribute('title');

    let tooltipEl: HTMLElement | null = null;

    const showTooltip = () => {
      if (!label || tooltipEl) return;
      tooltipEl = this.document.createElement('div');
      tooltipEl.textContent = label;
      tooltipEl.setAttribute('role', 'tooltip');
      tooltipEl.style.cssText = [
        'position:fixed',
        'background:#ffffff',
        'color:#000000',
        'border:1px solid #767676',
        'padding:3px 6px',
        'border-radius:2px',
        'font-size:12px',
        'line-height:1.4',
        'z-index:9999999',
        'pointer-events:none',
        'white-space:nowrap'
      ].join(';');
      this.document.body.appendChild(tooltipEl);
      const rect = btn.getBoundingClientRect();
      const tipRect = tooltipEl.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      left = Math.max(4, Math.min(left, window.innerWidth - tipRect.width - 4));
      tooltipEl.style.top = (rect.bottom + 4) + 'px';
      tooltipEl.style.left = left + 'px';
    };

    const hideTooltip = () => {
      if (tooltipEl) { tooltipEl.remove(); tooltipEl = null; }
    };

    btn.addEventListener('mouseenter', showTooltip);
    btn.addEventListener('mouseleave', hideTooltip);
    btn.addEventListener('focusin', showTooltip);
    btn.addEventListener('focusout', hideTooltip);
  }

 private fixPanelElements(panel: HTMLElement) {
    // Context-aware labels: prev/next button meaning changes with the active view
    const currentView = (this.pCalendarInstance as any)?.currentView || 'date';
    const prevLabel = currentView === 'month' ? 'Previous year'
                    : currentView === 'year'  ? 'Previous decade'
                    : 'Previous month';
    const nextLabel = currentView === 'month' ? 'Next year'
                    : currentView === 'year'  ? 'Next decade'
                    : 'Next month';

    const buttons = panel.querySelectorAll('button');
    buttons.forEach((btn: any) => {
      if (btn.classList.contains('p-datepicker-prev-button')) {
        btn.setAttribute('aria-label', prevLabel);
        btn.setAttribute('title', prevLabel);
        this.bindNavButtonKeyboardTooltip(btn);
        this.bindNavMonthAnnouncement(btn, panel);
      } else if (btn.classList.contains('p-datepicker-next-button')) {
        btn.setAttribute('aria-label', nextLabel);
        btn.setAttribute('title', nextLabel);
        this.bindNavButtonKeyboardTooltip(btn);
        this.bindNavMonthAnnouncement(btn, panel);
      } else {
        const text = btn.innerText?.trim();
        if (text) btn.setAttribute('aria-label', text);
      }
    });

    // Fix any anchor elements used as nav controls — expose them as buttons
    panel.querySelectorAll('.p-datepicker-header a, .p-datepicker-title a').forEach((a: any) => {
      a.setAttribute('role', 'button');
      if (!a.getAttribute('tabindex')) a.setAttribute('tabindex', '0');
    });

    // Remove invalid aria-labels from spans
    panel.querySelectorAll('td span').forEach((span: any) => {
      if (span.hasAttribute('aria-label')) span.removeAttribute('aria-label');
    });

    // 🔥 THE FIX: Broadened selectors to catch all PrimeNG view variations
    const selectableItems = panel.querySelectorAll(
      '.p-datepicker-day, .p-datepicker-month, .p-datepicker-year, .p-monthpicker-month, .p-yearpicker-year'
    );

    selectableItems.forEach((el: any) => {
      el.setAttribute('role', 'button'); // Satisfies the "widget role" requirement
      
      // Bonus: Ensure it has an aria-label so it doesn't read out blank
      if (!el.getAttribute('aria-label') && el.innerText) {
        el.setAttribute('aria-label', el.innerText.trim());
      }
    });
  }


private bindHeaderButtons(panel: HTMLElement) {
    const titleButtons = panel.querySelectorAll('.p-datepicker-title button');
    
    titleButtons.forEach((btn: any) => {
      if (!btn.hasAttribute('data-tg-bound')) {
        btn.setAttribute('data-tg-bound', 'true');
        
        this.renderer.listen(btn, 'click', () => {
          // 🔥 THE FIX: Using double requestAnimationFrame instead of setTimeout(100).
          // This guarantees the browser has finished painting the new Year/Month DOM 
          // before we try to add accessibility attributes to them.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.fixPanelElements(panel);
              this.handleFocusManagement(panel);
              
              // We must re-bind because PrimeNG destroys and recreates 
              // the header buttons themselves when switching views!
              this.bindHeaderButtons(panel); 
            });
          });
        });
      }
    });
  }

  private handleFocusManagement(panel: HTMLElement) {
    const applyFocus = () => {
      // Month View Focus
      const months = panel.querySelectorAll('.p-datepicker-month');
      if (months.length > 0) {
        months.forEach((m: any) => m.setAttribute('tabindex', '-1'));
        (months[0] as HTMLElement).setAttribute('tabindex', '0');
        setTimeout(() => (months[0] as HTMLElement).focus(), 50);
        return;
      }

      // Year View Focus
      // Year View Focus logic inside handleFocusManagement
      const years = panel.querySelectorAll('.p-datepicker-year, .p-yearpicker-year');
      if (years.length > 0) {
        years.forEach((y: any) => y.setAttribute('tabindex', '-1'));
        
        // Find current year or default to first
        const targetYear = panel.querySelector('.p-yearpicker-year.p-highlight, .p-datepicker-year.p-highlight') || years[0];
        
        (targetYear as HTMLElement).setAttribute('tabindex', '0');
        setTimeout(() => (targetYear as HTMLElement).focus(), 50);
        return;
      }

      // Date View Focus
      const days = panel.querySelectorAll('span.p-datepicker-day:not(.p-disabled)');
      if (days.length > 0) {
        days.forEach((d: any) => d.setAttribute('tabindex', '-1'));
        const target = panel.querySelector('span.p-highlight') || 
                       panel.querySelector('td.p-datepicker-today span.p-datepicker-day') || 
                       days[0];
        
        const el = target as HTMLElement;
        el.setAttribute('tabindex', '0');
        setTimeout(() => el.focus(), 0);
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(() => applyFocus()));
  }

  public onCalendarHide() {
    if (!this.calendarRef) return;
    const root = this.calendarRef.nativeElement;
    const buttonEl = root.querySelector('.p-datepicker-trigger');
    if (buttonEl) {
      this.renderer.setAttribute(buttonEl, 'aria-expanded', 'false');
    }
    if (this.pCalendarInstance) {
        setTimeout(() => {            
            this.pCalendarInstance.currentView = this.view === 'month' ? 'month' : 'date';
        }, 100);
    }
  }

  private announceToScreenReader(message: string) {
    this.a11yAnnouncement = ''; 
    setTimeout(() => {          
      this.a11yAnnouncement = message;
      setTimeout(() => { this.a11yAnnouncement = ''; }, 3000);
    }, 50);
  }
}
